import { h, createContext } from 'preact';
import { useEffect, useState, useMemo, useRef } from 'preact/hooks';
import useProductSiblings from 'utils/product-siblings';

const Context = createContext();

const ProductContext = ({ children, data }) => {
  const initialPageLoaded = useRef(false);
  const initialProduct = data?.product;

  if (!initialProduct) {
    // eslint-disable-next-line no-console
    console.error(
      'Theme Error: Please ensure this product is added to Online Store and Theme Access sales channels',
    );
    return {};
  }

  const hasOnlyDefaultVariant =
    initialProduct.hasOnlyDefaultVariant ||
    (initialProduct.options &&
      initialProduct.options.every((option) => option.values?.length === 1));

  const initialVariant = hasOnlyDefaultVariant
    ? initialProduct?.variants[0]
    : null;

  const initialOptions =
    initialProduct.variantBySelectedOptions?.selectedOptions.map((item) => ({
      ...item,
      value:
        initialProduct.variants.length > 1 &&
        item.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.size
          ? null
          : item.value,
    })) || [];

  const relatedProducts = useProductSiblings(initialProduct);

  const [products, setProducts] = useState([initialProduct]);
  const [currentProduct, setCurrentProduct] = useState(initialProduct);
  const [currentVariant, setCurrentVariant] = useState(initialVariant);
  const [options, setOptions] = useState(initialOptions);

  const fits = useMemo(
    () =>
      relatedProducts
        .reduce(
          (items, product) => [
            ...items,
            ...(product.options.find(
              (option) =>
                option.name.trim().toLowerCase() ===
                window.TMW.Strings.product.options.fit,
            )?.values || []),
          ],
          [],
        )
        .filter((value, index, array) => array.indexOf(value) === index)
        .sort()
        .reverse(),
    [relatedProducts],
  );

  const productService = {
    productState: {
      products,
      relatedProducts,
      currentProduct,
      currentVariant,
      options,
      fits,
    },
    productActions: {
      setCurrentProduct,
      setCurrentVariant,
      setOptions,
      setProducts,
    },
  };

  useEffect(() => {
    if (relatedProducts.length) {
      const fit = currentProduct.options.find(
        (option) =>
          option.name.trim().toLowerCase() ===
          window.TMW.Strings.product.options.fit,
      )?.values[0];
      if (fit) {
        setProducts(
          relatedProducts.filter((item) =>
            item.options
              .find(
                (option) =>
                  option.name.trim().toLowerCase() ===
                  window.TMW.Strings.product.options.fit,
              )
              ?.values.includes(fit),
          ),
        );
      } else {
        setProducts(relatedProducts);
      }
    }
  }, [relatedProducts]);

  useEffect(() => {
    const currentOptions = options.reduce(
      (items, item) => ({
        ...items,
        ...{
          [item.name]: item.value,
        },
      }),
      {},
    );
    setOptions([
      ...currentProduct.options.map((opt) => {
        let newValue = currentOptions[opt.name];
        if (!opt.values.includes(currentOptions[opt.name])) {
          newValue =
            opt.name.trim().toLowerCase() ===
              window.TMW.Strings.product.options.size && opt.values.length > 1
              ? null
              : opt.values[0];
        }
        return {
          name: opt.name,
          value: newValue,
        };
      }),
    ]);

    if (initialPageLoaded.current) {
      document.dispatchEvent(
        new CustomEvent('variant:change', { details: { currentProduct } }),
      );
    }
    initialPageLoaded.current = true;
  }, [currentProduct]);

  return (
    <Context.Provider value={{ ...productService, data }}>
      {children}
    </Context.Provider>
  );
};

export const withProductContext = (Rendered) => (props) =>
  (
    <Context.Consumer>
      {(value) => <Rendered {...props} {...value} />}
    </Context.Consumer>
  );

export default ProductContext;
